/**
 * THE SHEETS OF PEOPLE AND SIGN-IN (slice A3), which no artboard in `design/home` draws, so each is
 * built from drawn parts as Home's edit sheet is (`EditHome.tsx`): Schedule's sheet (`Sheet.tsx`),
 * Feedback's fields, the tick's box (`StripeWay.tsx`), Schedule's "Send the request" and the first
 * sentence of the line under it, and the code step (`FreshCode.tsx`). The words are `PEOPLE_SHEETS`
 * in `words.ts`, NEW for the owner to read.
 *
 * - FIVE ASKS, EACH THE OFFICE'S TO DECIDE (ops's section 4's `person.add` and section 12.4's
 *   four): a person to add, as a `member` or a `client`, for the homes she names; a person taken
 *   off; the homes a person sees; her own name, email or mobile; and her own door ended. Each goes
 *   to the door a fresh code stands in front of, `POST /api/requests/person` (CLAUDE.md binding rule
 *   5).
 * - A PERSON IS NAMED BY HOME'S REFERENCE, which the page was given for her, never by ops's id: the
 *   door finds her on the household's own copy and writes ops's id itself.
 * - WHAT SHE TYPES IS CHECKED FIRST, as the door checks it (`lib/people.ts`), and what to fix is said
 *   under its field, the first with the focus; nothing typed is compared with anything home holds.
 * - THE CODE STEP WHERE THE DOOR ASKS FOR ONE: every press goes to the door first, which asks for
 *   the code last, so an ask it would refuse is said in the sheet before any code is asked for, and
 *   its `403 step_up_required` draws the code step only for an ask it would take (round six, slice
 *   S1, in place of round four's code step at once where no fresh code stood, P59, which asked for a
 *   code the door could then refuse); once the code works, the same ask goes with the same key, so
 *   the door writes one row.
 * - ONE ASK HOWEVER OFTEN IT IS TAPPED, AND HOWEVER OFTEN THE SHEET IS CLOSED. Whether one is out,
 *   and its key, are held by the page (`lib/asks.ts`), never by the sheet.
 * - ASKING IS ALL IT DOES (binding rule 1). Sent is the page's to say, "Sent to the office.", and
 *   what comes after is ops's. What the door answers decides what follows: sent closes the sheet; a
 *   401 is the door; the same ask already waiting reads the page again and closes the sheet; a 404,
 *   what the ask is about gone or not hers to ask about now, which the door answers alike, is said
 *   on the ask with the phone and the focus, in the same words either way, the person named where
 *   the ask is about one (`PEOPLE.personNotHereBeforePhone`) and otherwise that it cannot be asked
 *   for here (`PEOPLE.cannotBeforePhone`), from the code step too, and reads the page again, the
 *   sheet staying open on it even where the page read again no longer lists the person, never
 *   closing in silence (the owner, 2026-10-20); a field that looks like it holds a code is said on
 *   that field; ops's `last_client` in its own words (section 12.5); her household past the door's
 *   limits, or anything else, with the phone, the sheet left open to try again.
 */
import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { sendRequest, type HomeName, type SignedInWith, type TooMany } from '../api.ts';
import { keyFor, type Asking } from '../lib/asks.ts';
import { noFreshCode } from '../lib/fresh.ts';
import { typedEmail, typedMobile } from '../lib/people.ts';
import {
  EDIT_HOME,
  FAILED_BEFORE_PHONE,
  LAST_CLIENT_BEFORE_PHONE,
  LOOKS_LIKE_A_CODE,
  PEOPLE,
  PEOPLE_SHEETS,
  SCHEDULE,
  tooManyBeforePhone,
} from '../words.ts';
import { NAVY_BUTTON } from './buttons.ts';
import { FreshCode, type ProvedCame } from './FreshCode.tsx';
import { Phone } from './Phone.tsx';
import { Sheet } from './Sheet.tsx';

/** The door every one of these asks goes to. */
const PERSON_DOOR = 'person';

/**
 * What the sheet says after a press that sent nothing it can end on; `not_here` is the door's 404,
 * what the ask is about no longer one she may ask about here.
 */
type Said = 'failed' | 'too_many' | 'last_client' | 'code' | 'not_here';

interface Line {
  readonly said: Said;
  readonly count: number;
  /** Past the door's own limits: whose count and when, as the door said them (slice S1). */
  readonly limit?: TooMany;
}

/** What every sheet here is told by the page that opens it. */
export interface PeopleSheetProps {
  /** The household the page shows, which the ask is about. */
  readonly household: string;
  /** The address or mobile number she signed in with, which a fresh code goes to (home #11). */
  readonly signedInWith: SignedInWith;
  /** Whether this ask is out, and its keys, held by the page. */
  readonly asking: Asking;
  /** Closes the sheet, having asked nothing more. */
  readonly onClose: () => void;
  /** The door wrote the ask, whose id this is, with what it carried. */
  readonly onSent: (id: string, asked: Readonly<Record<string, unknown>>) => void;
  /** The session has ended. */
  readonly onSignedOut: () => void;
  /** What the page shows may be out of date: read it again. */
  readonly onStale: () => void;
}

interface AskSheetProps extends PeopleSheetProps {
  readonly heading: string;
  /** The person the ask is about, by name, drawn under the heading. */
  readonly about?: string;
  readonly type: string;
  /** Why a fresh code is asked for, said first on the code step. */
  readonly why: string;
  /** What is said, before the phone, where no fresh code can be asked for here. */
  readonly closedBeforePhone: string;
  /**
   * What is said, before the phone, on the door's 404, the same whether what the ask is about is
   * gone or never hers (binding rule 2); `PEOPLE.cannotBeforePhone` unless the ask names a person.
   */
  readonly notHereBeforePhone?: string;
  /** What is sent, or null where the fields say what to fix first, which they then say. */
  readonly payload: () => Readonly<Record<string, unknown>> | null;
  /** Whether anything is there to send: the button waits until it is. */
  readonly ready: boolean;
  /** The lines under the fields. */
  readonly notes: readonly string[];
  /** Ops would refuse it as `last_client`: its words are said in place of the button. */
  readonly lastClient?: boolean;
  /** The door refused a field as looking like a code: the sheet marks it. */
  readonly onCode?: (field: string) => void;
  readonly children?: ReactNode;
}

/** Ops's sentence for `last_client`, with the office's number as a link to call it. */
export function LastClient() {
  return (
    <>
      {LAST_CLIENT_BEFORE_PHONE} <Phone after="." />
    </>
  );
}

/** One ask's sheet: its fields, then the code step where one is needed, then the door. */
function AskSheet({
  heading,
  about,
  type,
  household,
  signedInWith,
  asking,
  why,
  closedBeforePhone,
  notHereBeforePhone = PEOPLE.cannotBeforePhone,
  payload,
  ready,
  notes,
  lastClient = false,
  onCode,
  onClose,
  onSent,
  onSignedOut,
  onStale,
  children,
}: AskSheetProps) {
  const [step, setStep] = useState<'ask' | 'code'>('ask');
  const [line, setLine] = useState<Line | null>(null);
  const [busy, setBusy] = useState(false);
  const lines = useRef(0);
  /** What the press asked for, sent again once the code works. */
  const pending = useRef<Readonly<Record<string, unknown>> | null>(null);
  /** What the sheet says of the door's 404, which takes the focus. */
  const notHereLine = useRef<HTMLParagraphElement>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // What the ask is about is no longer hers to ask about here: what the sheet says of it takes the
  // focus, so it is read out and stays in the sheet, where Escape still closes it, even where the
  // page read again takes the button that opened it away.
  useEffect(() => {
    if (line?.said === 'not_here') notHereLine.current?.focus();
  }, [line]);

  const say = (said: Said, limit?: TooMany) => {
    lines.current += 1;
    setLine({ said, count: lines.current, limit });
  };

  /** Sends what she asked, with the key the page holds for it, and acts on what the door says. */
  async function send(asked: Readonly<Record<string, unknown>>): Promise<ProvedCame> {
    asking.out = true;
    const answer = await sendRequest(
      { key: keyFor(asking, JSON.stringify({ type, asked })), type, household, payload: asked },
      PERSON_DOOR,
    );
    asking.out = false;
    switch (answer.outcome) {
      case 'sent':
        asking.keys.clear();
        onSent(answer.id, asked);
        return null;
      case 'step_up':
        noFreshCode();
        if (mounted.current) setStep('code');
        return 'step_up';
      case 'signed_out':
        onSignedOut();
        return null;
      case 'already_asked':
        onStale();
        if (mounted.current) onClose();
        return null;
      // What the ask is about is gone, or not hers to ask about now, which the door answers alike
      // (binding rule 2): said on the ask, in the same words either way, never silent, and the page
      // read again; from the code step too, which has nothing more to do.
      case 'not_found':
        onStale();
        if (mounted.current) {
          setStep('ask');
          say('not_here');
        }
        return null;
      case 'looks_like_a_code':
        if (mounted.current) {
          setStep('ask');
          say('code');
          onCode?.(answer.field);
        }
        return null;
      case 'too_many':
        return { tooMany: answer.limit };
      case 'refused':
        if (answer.code === 'last_client') {
          if (mounted.current) {
            setStep('ask');
            say('last_client');
          }
          return null;
        }
        return 'failed';
      default:
        return 'failed';
    }
  }

  async function press(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (asking.out || !ready) return;
    const asked = payload();
    if (asked === null) {
      setLine(null);
      return;
    }
    pending.current = asked;
    setLine(null);
    // The door is asked first, whether or not a fresh code stands: it asks for the code last, so a
    // press it would refuse is said here before any code is asked for, and its 403 draws the code
    // step only for an ask it would take (round six, slice S1).
    setBusy(true);
    const came = await send(asked);
    if (!mounted.current) return;
    setBusy(false);
    if (came === 'failed') say(came);
    else if (came !== null && typeof came === 'object') say('too_many', came.tooMany);
  }

  /** Once the code works: the same ask again, and what the code step says if it does not go. */
  async function proved(): Promise<ProvedCame> {
    if (asking.out || pending.current === null) return null;
    return send(pending.current);
  }

  return (
    <Sheet
      heading={heading}
      sub={about === undefined ? undefined : <p className="text-small text-text-muted">{about}</p>}
      onClose={onClose}
    >
      {step === 'code' ? (
        <FreshCode
          signedInWith={signedInWith}
          why={why}
          submit={SCHEDULE.send}
          closedBeforePhone={closedBeforePhone}
          onProved={proved}
          className="mt-4"
        />
      ) : (
        // The sheet says what to fix in its own words, under each field, so the browser's own
        // checks of an address field do not stand in front of them.
        <form
          method="post"
          noValidate
          onSubmit={(event) => void press(event)}
          className="mt-4 flex flex-col"
        >
          {children}
          {notes.map((note) => (
            <p key={note} className="mt-4 text-small text-pretty text-text-muted">
              {note}
            </p>
          ))}
          <div aria-live="polite">
            {line === null ? null : (
              <p
                key={line.count}
                ref={line.said === 'not_here' ? notHereLine : undefined}
                tabIndex={line.said === 'not_here' ? -1 : undefined}
                className="mt-4 text-body text-pretty text-text-body"
              >
                {line.said === 'code' ? (
                  LOOKS_LIKE_A_CODE
                ) : line.said === 'last_client' ? (
                  <LastClient />
                ) : (
                  <>
                    {line.said === 'too_many'
                      ? tooManyBeforePhone(line.limit)
                      : line.said === 'not_here'
                        ? notHereBeforePhone
                        : FAILED_BEFORE_PHONE}{' '}
                    <Phone after="." />
                  </>
                )}
              </p>
            )}
          </div>
          {lastClient ? (
            <p className="mt-4 text-body text-pretty text-text-body">
              <LastClient />
            </p>
          ) : (
            <button
              type="submit"
              disabled={!ready}
              aria-disabled={busy ? true : undefined}
              className={`mt-6 cursor-pointer aria-disabled:cursor-wait aria-disabled:opacity-70 disabled:cursor-not-allowed disabled:opacity-60 ${NAVY_BUTTON}`}
            >
              {SCHEDULE.send}
            </button>
          )}
        </form>
      )}
    </Sheet>
  );
}

/** Feedback's field, as one line. */
const FIELD =
  'mt-2 min-h-tap-call rounded-control border-2 border-border-strong bg-surface-raised px-4 py-3 ' +
  'text-body text-text-body';

const QUESTION = 'text-body font-semibold text-text-body';

/** What to fix, said under the field it is about. */
const FIX = 'mt-2 text-small text-pretty text-text-body';

/** The tick's box and its words, a target at least 48 tall. */
const BOX_LABEL =
  'grid min-h-tap cursor-pointer grid-cols-[auto_1fr] content-center items-start gap-3 text-body ' +
  'text-text-body';
const BOX = 'mt-1 size-6 cursor-pointer accent-surface-brand';

interface FieldProps {
  readonly label: string;
  readonly type: 'text' | 'email' | 'tel';
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly autoComplete: string;
  readonly maxLength?: number;
  /** What to fix in it, said under it, or null. */
  readonly fix: string | null;
  readonly fieldRef: (field: HTMLInputElement | null) => void;
  readonly first?: boolean;
}

/** A field with its label above it and what to fix under it, which it is described by. */
function Field({
  label,
  type,
  value,
  onChange,
  autoComplete,
  maxLength,
  fix,
  fieldRef,
  first = false,
}: FieldProps) {
  const id = useId();
  const fixId = useId();
  return (
    <div className={`flex flex-col ${first ? '' : 'mt-5'}`}>
      <label htmlFor={id} className={QUESTION}>
        {label}
      </label>
      <input
        ref={fieldRef}
        id={id}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        autoComplete={autoComplete}
        maxLength={maxLength}
        aria-invalid={fix === null ? undefined : true}
        aria-describedby={fix === null ? undefined : fixId}
        className={FIELD}
      />
      {fix === null ? null : (
        <p id={fixId} className={FIX}>
          {fix}
        </p>
      )}
    </div>
  );
}

interface HomesProps {
  readonly homes: readonly HomeName[];
  readonly chosen: ReadonlySet<string>;
  readonly onChange: (chosen: ReadonlySet<string>) => void;
  readonly fix: string | null;
  readonly firstBox: (box: HTMLInputElement | null) => void;
  /** A line under the boxes that says what the homes mean, which the question is described by. */
  readonly hint?: string;
}

/**
 * "Which homes can they see?", a box for each home she may name by the label ops gave it: one ops
 * sent no label for has no box, since a box named by an id would put the id on the screen.
 */
function Homes({ homes, chosen, onChange, fix, firstBox, hint }: HomesProps) {
  const fixId = useId();
  const hintId = useId();
  const labelled = homes.filter(
    (home): home is HomeName & { readonly label: string } => home.label !== undefined,
  );
  const describedBy = [hint === undefined ? null : hintId, fix === null ? null : fixId]
    .filter((id): id is string => id !== null)
    .join(' ');
  return (
    <fieldset className="mt-5 flex flex-col" aria-describedby={describedBy || undefined}>
      <legend className={QUESTION}>{PEOPLE_SHEETS.whichHomes}</legend>
      {labelled.map((home, index) => (
        <label key={home.id} className={`mt-1 ${BOX_LABEL}`}>
          <input
            ref={index === 0 ? firstBox : undefined}
            type="checkbox"
            checked={chosen.has(home.id)}
            onChange={(event) => {
              const next = new Set(chosen);
              if (event.target.checked) next.add(home.id);
              else next.delete(home.id);
              onChange(next);
            }}
            className={BOX}
          />
          <span className="text-pretty">{home.label}</span>
        </label>
      ))}
      {hint === undefined ? null : (
        <p id={hintId} className="mt-2 text-small text-pretty text-text-muted">
          {hint}
        </p>
      )}
      {fix === null ? null : (
        <p id={fixId} className={FIX}>
          {fix}
        </p>
      )}
    </fieldset>
  );
}

/** The homes she named, in the order the page lists them. */
const inOrder = (homes: readonly HomeName[], chosen: ReadonlySet<string>) =>
  homes.filter((home) => chosen.has(home.id)).map((home) => home.id);

/** What to fix in the fields of a person to add, by field. */
interface AddFixes {
  readonly name: string | null;
  readonly email: string | null;
  readonly mobile: string | null;
  readonly homes: string | null;
}

const NO_FIXES: AddFixes = { name: null, email: null, mobile: null, homes: null };

/**
 * ASK TO ADD SOMEONE: her name, her email or her mobile number or both, whether she may pay and
 * change visits too or only see them, and the homes she sees, of those a granted client door of hers
 * covers; with one home, it is chosen and not asked. For one who pays (round six, slice S2): every
 * home ticked to start where none is chosen yet; a line under the homes that says she can pay every
 * invoice of the household whichever homes she sees, which is what a client door does at home and
 * at ops; and "as you can" only while she would see every home, since with some she could not do
 * all the asker can.
 */
export function AddPersonSheet({
  homes,
  ...props
}: PeopleSheetProps & { readonly homes: readonly HomeName[] }) {
  const roleName = useId();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [role, setRole] = useState<'member' | 'client'>('member');
  const [chosen, setChosen] = useState<ReadonlySet<string>>(
    () => new Set(homes.length === 1 ? [homes[0]!.id] : []),
  );
  const [fixes, setFixes] = useState<AddFixes>(NO_FIXES);
  const fields = useRef(new Map<keyof AddFixes, HTMLInputElement>());
  const keep = (field: keyof AddFixes) => (element: HTMLInputElement | null) => {
    if (element) fields.current.set(field, element);
    else fields.current.delete(field);
  };

  function payload(): Record<string, unknown> | null {
    const named = name.trim();
    const address = email.trim() === '' ? null : typedEmail(email);
    const number = mobile.trim() === '' ? null : typedMobile(mobile);
    const neither = email.trim() === '' && mobile.trim() === '';
    const found: AddFixes = {
      name: named === '' ? PEOPLE_SHEETS.noName : null,
      email: neither
        ? PEOPLE_SHEETS.noContact
        : email.trim() !== '' && address === null
          ? PEOPLE_SHEETS.badEmail
          : null,
      mobile: mobile.trim() !== '' && number === null ? PEOPLE_SHEETS.badMobile : null,
      homes: chosen.size === 0 ? PEOPLE_SHEETS.noHomes : null,
    };
    setFixes(found);
    const first = (['name', 'email', 'mobile', 'homes'] as const).find(
      (field) => found[field] !== null,
    );
    if (first !== undefined) {
      fields.current.get(first)?.focus();
      return null;
    }
    return {
      display_name: named,
      ...(address === null ? {} : { email: address }),
      ...(number === null ? {} : { phone: number }),
      role,
      homes: inOrder(homes, chosen),
    };
  }

  /** The homes she can tick: each with a label, or the one home, which is chosen and not asked. */
  const choosable = homes.length === 1 ? homes : homes.filter((home) => home.label !== undefined);
  /** Some of her homes chosen, and not all: one who pays could not do all she can. */
  const some = chosen.size > 0 && choosable.some((home) => !chosen.has(home.id));
  const chooseRole = (each: 'member' | 'client') => {
    setRole(each);
    if (each === 'client' && chosen.size === 0) {
      setChosen(new Set(choosable.map((home) => home.id)));
    }
  };

  return (
    <AskSheet
      {...props}
      heading={PEOPLE.add}
      type="person.add"
      why={PEOPLE_SHEETS.addFreshCode}
      closedBeforePhone={PEOPLE_SHEETS.addCannotBeforePhone}
      payload={payload}
      ready
      notes={[PEOPLE_SHEETS.invitation, EDIT_HOME.nothingChanges]}
      onCode={(field) => {
        if (field !== 'display_name') return;
        setFixes((held) => ({ ...held, name: LOOKS_LIKE_A_CODE }));
        fields.current.get('name')?.focus();
      }}
    >
      <Field
        first
        label={PEOPLE_SHEETS.theirName}
        type="text"
        value={name}
        onChange={setName}
        autoComplete="off"
        maxLength={100}
        fix={fixes.name}
        fieldRef={keep('name')}
      />
      <Field
        label={PEOPLE_SHEETS.theirEmail}
        type="email"
        value={email}
        onChange={setEmail}
        autoComplete="off"
        maxLength={254}
        fix={fixes.email}
        fieldRef={keep('email')}
      />
      <Field
        label={PEOPLE_SHEETS.theirMobile}
        type="tel"
        value={mobile}
        onChange={setMobile}
        autoComplete="off"
        fix={fixes.mobile}
        fieldRef={keep('mobile')}
      />
      <fieldset className="mt-5 flex flex-col">
        <legend className={QUESTION}>{PEOPLE_SHEETS.whatCanTheyDo}</legend>
        {(['member', 'client'] as const).map((each) => (
          <label key={each} className={`mt-1 ${BOX_LABEL}`}>
            <input
              type="radio"
              name={roleName}
              checked={role === each}
              onChange={() => chooseRole(each)}
              className={BOX}
            />
            <span className="text-pretty">
              {each === 'client' && some ? PEOPLE_SHEETS.clientSomeHomes : PEOPLE_SHEETS[each]}
            </span>
          </label>
        ))}
      </fieldset>
      {homes.length > 1 ? (
        <Homes
          homes={homes}
          chosen={chosen}
          onChange={setChosen}
          fix={fixes.homes}
          firstBox={keep('homes')}
          {...(role === 'client' ? { hint: PEOPLE_SHEETS.payerPaysAll } : {})}
        />
      ) : null}
    </AskSheet>
  );
}

/**
 * ASK TO CHANGE WHAT THEY SEE: the homes a person sees, ticked as they are now, every one for a
 * person who sees them all; sent only once she has changed them, and at least one.
 */
export function ChangeHomesSheet({
  name,
  personRef,
  current,
  homes,
  ...props
}: PeopleSheetProps & {
  readonly name: string | undefined;
  readonly personRef: string;
  /** The homes the person sees now by id, or undefined for every home. */
  readonly current: readonly string[] | undefined;
  readonly homes: readonly HomeName[];
}) {
  const [initial] = useState<ReadonlySet<string>>(
    () =>
      new Set(
        homes
          .filter((home) => current === undefined || current.includes(home.id))
          .map((home) => home.id),
      ),
  );
  const [chosen, setChosen] = useState<ReadonlySet<string>>(initial);
  const [fix, setFix] = useState<string | null>(null);
  const firstBox = useRef<HTMLInputElement | null>(null);
  const changed =
    chosen.size !== initial.size || [...chosen].some((homeId) => !initial.has(homeId));

  function payload(): Record<string, unknown> | null {
    if (chosen.size === 0) {
      setFix(PEOPLE_SHEETS.noHomes);
      firstBox.current?.focus();
      return null;
    }
    setFix(null);
    return { person: personRef, homes: inOrder(homes, chosen) };
  }

  return (
    <AskSheet
      {...props}
      heading={PEOPLE.changeWhatTheySee}
      about={name}
      type="person.change"
      why={PEOPLE_SHEETS.changeFreshCode}
      closedBeforePhone={PEOPLE.cannotBeforePhone}
      notHereBeforePhone={PEOPLE.personNotHereBeforePhone}
      payload={payload}
      ready={changed}
      notes={[PEOPLE_SHEETS.newInvitation, EDIT_HOME.nothingChanges]}
    >
      <Homes
        homes={homes}
        chosen={chosen}
        onChange={setChosen}
        fix={fix}
        firstBox={(box) => {
          firstBox.current = box;
        }}
      />
    </AskSheet>
  );
}

/** ASK TO TAKE THEM OFF: what it comes to, and nothing to fill in. */
export function TakeOffSheet({
  name,
  personRef,
  ...props
}: PeopleSheetProps & { readonly name: string | undefined; readonly personRef: string }) {
  return (
    <AskSheet
      {...props}
      heading={PEOPLE.takeThemOff}
      about={name}
      type="person.remove"
      why={PEOPLE_SHEETS.takeOffFreshCode}
      closedBeforePhone={PEOPLE.cannotBeforePhone}
      notHereBeforePhone={PEOPLE.personNotHereBeforePhone}
      payload={() => ({ person: personRef })}
      ready
      notes={[PEOPLE_SHEETS.takeOffLine, EDIT_HOME.nothingChanges]}
    />
  );
}

/** What to fix in her own details, by field. */
interface ContactFixes {
  readonly name: string | null;
  readonly email: string | null;
  readonly mobile: string | null;
}

/**
 * ASK TO CHANGE MY NAME, EMAIL OR MOBILE: her name as the office typed it, to change in place, and a
 * new email or number; only what she changed is sent, and nothing until she has changed something.
 */
export function ContactSheet({
  name: typedName,
  ...props
}: PeopleSheetProps & { readonly name: string | undefined }) {
  const was = (typedName ?? '').trim();
  const [name, setName] = useState(typedName ?? '');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [fixes, setFixes] = useState<ContactFixes>({ name: null, email: null, mobile: null });
  const fields = useRef(new Map<keyof ContactFixes, HTMLInputElement>());
  const keep = (field: keyof ContactFixes) => (element: HTMLInputElement | null) => {
    if (element) fields.current.set(field, element);
    else fields.current.delete(field);
  };
  const newName = name.trim() !== '' && name.trim() !== was ? name.trim() : null;
  const ready = newName !== null || email.trim() !== '' || mobile.trim() !== '';

  function payload(): Record<string, unknown> | null {
    const address = email.trim() === '' ? null : typedEmail(email);
    const number = mobile.trim() === '' ? null : typedMobile(mobile);
    const found: ContactFixes = {
      name: null,
      email: email.trim() !== '' && address === null ? PEOPLE_SHEETS.badEmail : null,
      mobile: mobile.trim() !== '' && number === null ? PEOPLE_SHEETS.badMobile : null,
    };
    setFixes(found);
    const first = (['email', 'mobile'] as const).find((field) => found[field] !== null);
    if (first !== undefined) {
      fields.current.get(first)?.focus();
      return null;
    }
    return {
      ...(newName === null ? {} : { display_name: newName }),
      ...(address === null ? {} : { email: address }),
      ...(number === null ? {} : { phone: number }),
    };
  }

  return (
    <AskSheet
      {...props}
      heading={PEOPLE.changeMine}
      type="person.contact_change"
      why={PEOPLE_SHEETS.contactFreshCode}
      closedBeforePhone={PEOPLE.cannotBeforePhone}
      payload={payload}
      ready={ready}
      notes={[PEOPLE_SHEETS.newEmailSignIn, EDIT_HOME.nothingChanges]}
      onCode={(field) => {
        if (field !== 'display_name') return;
        setFixes((held) => ({ ...held, name: LOOKS_LIKE_A_CODE }));
        fields.current.get('name')?.focus();
      }}
    >
      <Field
        first
        label={PEOPLE_SHEETS.yourName}
        type="text"
        value={name}
        onChange={setName}
        autoComplete="name"
        maxLength={100}
        fix={fixes.name}
        fieldRef={keep('name')}
      />
      <Field
        label={PEOPLE_SHEETS.yourNewEmail}
        type="email"
        value={email}
        onChange={setEmail}
        autoComplete="email"
        maxLength={254}
        fix={fixes.email}
        fieldRef={keep('email')}
      />
      <Field
        label={PEOPLE_SHEETS.yourNewMobile}
        type="tel"
        value={mobile}
        onChange={setMobile}
        autoComplete="tel"
        fix={fixes.mobile}
        fieldRef={keep('mobile')}
      />
    </AskSheet>
  );
}

/**
 * ASK TO REMOVE MY ACCESS TO THIS PAGE, her own leave (round six, slice S2, in place of "Take me off
 * home", which read as her house): what it comes to, and nothing to fill in. The page never offers
 * it to the household's only client at home; should a page read again make her that while the sheet
 * is open, ops's own words stand in place of the button, since ops would refuse it (`last_client`).
 */
export function LeaveSheet({
  lastClient,
  ...props
}: PeopleSheetProps & { readonly lastClient: boolean }) {
  return (
    <AskSheet
      {...props}
      heading={PEOPLE.takeMeOff}
      type="person.leave"
      why={PEOPLE_SHEETS.leaveFreshCode}
      closedBeforePhone={PEOPLE.cannotBeforePhone}
      payload={() => ({})}
      ready
      lastClient={lastClient}
      notes={lastClient ? [] : [PEOPLE_SHEETS.leaveLine, EDIT_HOME.nothingChanges]}
    />
  );
}
